import { Dispatch, SetStateAction } from "react";
import { ParametricType } from "src/shared/types";

export type FineParametersResponse = {
  violationTypes: ParametricType[];
  companyEntityTypes: ParametricType[];
  legalTypes: ParametricType[];
  companyStatuses: ParametricType[];
  violationRepeatTypes: ParametricType[];
  companySizeTypes: ParametricType[];
  preselectedFineParametrics: PreselectedFineParametrics;
};

export type FineCalculatorFormValues = {
  violationType: number;
  companyEntityType: number;
  legalType: number;
  companyStatus: number;
  violationRepeatType: number;
  companySizeType: number;
};

export type DashboardParametricsResponse = {
  violationTypes: ParametricType[];
  violationSourceTypes?: ParametricType[];
  violationCaseStatuses: ParametricType[];
  chambers?: ParametricType[];
  organizations?: ParametricType[];
  chamberAdmins?: ParametricType[];
  corporateStatuses?: ParametricType[];
};

export type ChamberDashboardResults = {
  id: number;
  companyId: number;
  afm: string;
  companyName: string;
  violationTypeDescription: string;
  violationSourceTypeDescription: string;
  dateSubmitted: string;
  fineDateSubmitted: string;
  violationCaseStatusDescription: string;
  violationCasePhaseDescription: string;
  assigneeChamberUserDescription: string;
  violationCaseStepId: number;
  violationCaseStatusId: number;
  violationCasePhaseId: number;
  corporateStatusId: number;
  corporateStatusDescription: string;
  pendingStatusesDto: PendingStatuses;
  expiryDays: number;
};

export type SupervisoryViolationResults = {
  id: number;
  companyId: number;
  afm: string;
  companyName: string;
  violationTypeDescription: string;
  violationSourceTypeDescription: string;
  dateSubmitted: string;
  fineDateSubmitted: string;
  violationCaseStatusDescription: string;
  violationCaseStatusId: number;
  organizationDescription: string;
};

export type PendingStatuses = {
  assignmentPending: boolean;
  supervisorAssignmentPending: boolean;
  correctionPending: boolean;
  paymentPending: boolean;
  onTimePayment: boolean;
  latePayment: boolean;
  disputeProposal: boolean;
  fineCalculationPending: boolean;
  complianceInstructionsPending: boolean;
  disputeImpediment: boolean;
  acceptedViolationPayFull: boolean;
  acceptanceDate: string | null;
};

export type DashboardResultsResponse = {
  totalResults: number;
  chamberDashboardResultsDto?: ChamberDashboardResults[];
  companyDashboardResultsDto?: CompanyDashboardResults[];
  adminDashboardResultsDto?: AdminDashboardResults[];
};

export type TSearchFilters = {
  violationTypeId?: number[];
  violationSourceId?: number[];
  violationCaseStatusId?: number[];
  assigneeChamberUserId?: number[];
  violationCaseId?: number;
  companyId?: number;
  assigneeChamberAdminId?: number[];
  organizationId?: number;
  dateSubmittedFrom?: Date;
  dateSubmittedTo?: Date;
  fineDateSubmittedFrom?: Date;
  fineDateSubmittedTo?: Date;
  corporateStatusId?: number[];
};

export type SupervisoryTeamViolationResult = {
  id: number;
  companyId: number;
  afm: string;
  companyName: string;
  violationTypeDescription: string;
  violationSourceTypeDescription: string;
  dateSubmitted: string;
  fineDateSubmitted: string;
  violationCaseStatusDescription: string;
  organizationDto: { id: number; label: string };
};

export type SupervisoryTeamViolationResponse = {
  violations: SupervisoryTeamViolationResult[];
  totalResults: number;
};

export enum ViolationStepEnum {
  DRAFT = 1,
  CONFIRMED_VIOLATION = 2,
  CORRECTION_UNDER_REVIEW = 3,
  DISPUTE_UNDER_REVIEW = 4,
  CORRECTION_PAYMENT_UNDER_REVIEW_FIRST_PHASE = 5,
  CALL_TO_HEARING = 6,
  DELETE_VIOLATION_CASE = 7,
  TO_FINALIZE_FINE = 8,
  FINE_FINALIZED = 9,
  COMPLETE_WITHOUT_FINE_CORRECTED_VIOLATION = 10,
  COMPLETE_WITHOUT_FINE_APPROVED_DISPUTE = 11,
  FINALIZE_WITH_PAYMENT = 12,
  FINALIZE_WITH_PAYMENT_50 = 13,
  FINALIZE_WITH_PAYMENT_100 = 14,
  CALL_TO_CORRECT = 15,
  CORRECTION_PAYMENT_UNDER_REVIEW_SECOND_PHASE = 16,
}

// Synthetic id for the combined "pending payment" business filter option.
// Expands to FINALIZE_WITH_PAYMENT_50 (13) + FINALIZE_WITH_PAYMENT_100 (14) at request time.
export const PENDING_PAYMENT_COMBINED_ID = -13;

export type TCompanyActionBarProps = {
  caseId: string;
  stepId: number;
  isPhaseOneCorrectionSubmitted: boolean;
  payments?: ViolationFinePayment[];
  violationTypeId?: number;
};

export type TCorrectionModalBodyProps = {
  caseId: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
  companyId: string;
};

export type TEvaluateDisputeModalBodyProps = {
  caseId: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export type TCorrectionPayload = {
  companyReqText: string;
  correctionFile?: File;
};

export type ViolationParametrics = {
  violationSourceTypes: ParametricType[];
  violationTypes: ParametricType[];
  violationCaseStatuses?: ParametricType[];
  chambers?: ParametricType[];
};

export type CompanyInfo = {
  afm: string;
  coName: string;
  companyAddress: string;
  companyEmail: string;
  companyId: number;
  companyPhone: string;
};

export enum CorrectionStatusEnum {
  FULL_CORRECTION = 1,
  INSUFFICIENT_CORRECTION = 2,
}

export type CorrectionData = {
  chamberResFile: string | null;
  chamberResText: string | null;
  companyReqFile: string | null;
  companyReqText: string | null;
  correctionId: number;
  correctionPhaseId: number;
  correctionPhaseDescription: string;
  correctionStatusId: number | null;
  userSelectedApplications?: {
    applicationId: number;
    applicationDescription: string;
    submissionDate: string;
    applicationStatusDescription: string;
  };
};

export type CompanyDashboardResults = {
  id: number;
  violationTypeDescription: string;
  violationSourceTypeDescription: string;
  violationCaseStatusDescription: string;
  violationCasePhaseDescription: string;
  expiryDays: number;
  violationCaseStepId: number;
  violationCaseStatusId: number;
  violationCasePhaseId: number;
  pendingStatusesDto: PendingStatuses;
  debtAmount?: number;
};

export type AdminDashboardResults = {
  id: number;
  companyId: number;
  afm: string;
  companyName: string;
  violationTypeDescription: string;
  violationSourceTypeDescription: string;
  dateSubmitted: string;
  fineDateSubmitted: string;
  violationCaseStatusDescription: string;
  violationCasePhaseDescription: string;
  organizationDescription: string;
  violationCaseStepId: number;
  violationCaseStatusId: number;
  violationCasePhaseId: number;
  corporateStatusId: number;
  corporateStatusDescription: string;
  pendingStatusesDto: PendingStatuses;
  expiryDays: number;
};

export type TReviewCorrectionPayload = {
  chamberReqText: string;
  correctionStatusId: string;
  correctionFile?: File;
  applicationIds?: number[];
};

export type TEvaluateDisputePayload = {
  chamberReqText: string;
  evaluateDisputeStatusId: string;
  evaluateDisputeFileName?: File;
};

export enum DisputeTypeEnum {
  VIOLATION_DISPUTE = 1,
  FINE_DISPUTE = 2,
  IMPEDIMENT,
}

export enum DisputeResponseEnum {
  FULL_APPROVAL = 1,
  REJECTION = 2,
  FINE_MODIFICATION_APPROVAL = 3,
}

export type ViolationCaseStep = {
  stepId: number;
  statusId: number;
  statusDescription: string;
  phaseId: number;
  phaseDescription: string;
};

export type CompanyData = {
  companyId: string;
  afm: string;
  coName: string;
  companyAddress: string;
  companyEmail: string;
  companyPhone: string;
};

export type ViolationFinePayment = {
  paymentMethod: ParametricType;
  paymentDate: string | null;
  paymentDeclaredDate: string | null;
  paymentAmount: number;
  invoiceFileName: string | null;
  ri3: string | null;
  paymentType: string | null;
  paymentStatus: "confirmed" | "late" | "unconfirmed" | null;
};

export type BasicViolationDetails = {
  violationType: ParametricType;
  violationSourceType: ParametricType;
  chamberComplianceInstruction: string;
  fineAmount: number;
  companyEntityType: ParametricType;
  corporateStatus: ParametricType;
  companySizeType: ParametricType;
  companyStatus: ParametricType;
  violationRepeatType: ParametricType;
  fineViolationType: ParametricType;
  isCorrectionSubmittedAndNotPaid: boolean;
  chamberComplianceFile: string | undefined;
  financialYear: number | undefined | null;
  thirdPartyFile: string | undefined;
  chamberId: number | undefined;
  payments: ViolationFinePayment[];
};

export type ViolationCaseAssignWorkflow = {
  assigner: string;
  assigneeChamberAdmin: string;
  assigneeChamberUser: string;
  message: string;
  dateCreated: Date;
};
export type ViolationCaseEventWorkflow = {
  actor: string;
  dateCreated: Date;
  event: string;
  phaseDescription: string;
  phaseId: number;
  statusDescription: string;
};

export type ProcessingData = {
  assigneeChamberUser?: ParametricType;
  assigneeChamberAdmin?: ParametricType;
  dateSubmitted: string;
  fineDateSubmitted: string;
  violationCaseStep: ViolationCaseStep;
  chamberNotes?: string;
  deleteViolationCaseText?: string;
  deleteViolationCaseFile?: string;
  chamberProposalText?: string | null;
  expiryDays?: number;
};

export type DetailsView = {
  processingData: ProcessingData;
  companyData?: CompanyData;
  basicViolationDetails: BasicViolationDetails;
  pendingStatusesDto: PendingStatuses;
};

export type ComplaintDataForNewViolationCase = {
  companyId: string;
  coName: string;
  afm: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  violationTypeId: number;
  financialYear: number | undefined;
};

export type SampleCompanyCaseDataForNewViolationCase = {
  companyId: string;
  coName: string;
  afm: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  violationTypeId: number;
};

export enum ViolationSourceEnum {
  AUTOMATIC = 1,
  COMPLAINT = 2,
  SAMPLE_INSPECTION = 3,
  DUTY_DETECTION = 4,
  PUBLIC_AUTHORITY_NOTIFICATION = 5,
}

export enum CorrectionAndPaymentEvaluationStatusEnum {
  FULL_CORRECTION = 1,
  INSUFFICIENT_CORRECTION = 2,
}

export enum PaymentStatusEnum {
  PAYMENT_CONFIRMED = 14,
  PAYMENT_UNCONFIRMED = 15,
  PAYMENT_LATE = 16,
}

export type UpdateCorrectionData = {
  violationCaseStepId: number;
  paymentResult: number;
  isUnconfirmedPaymentAfterDeadline: boolean;
  daysFromPaymentFineSubmission: number;
};

export type SubmitRating = {
  violationCaseId: string;
  decisionOfCorrection: string;
  chamberResponseText: string;
  chamberResponseFile?: File;
};

export enum DashboardSectionEnum {
  CHAMBER_ADMIN = 1,
  ADMIN = 2,
  ADMIN_ASSIGNED = 3,
  ADMIN_UNASSIGNED = 4,
  EMPLOYEE = 5,
  EMPLOYEE_COMPLETED = 6,
  SAMPLE_INITIATOR = 7,
  CHAMBER_ADMIN_COMPLETED = 8,
  CHAMBER_ADMIN_ACTIVE = 9,
  CHAMBER_SEARCH_BY_COMPANY_ID = 10,
  SUPERVISORY_TEAM_ASSIGNEE = 11,
}

export type TDisputeModalBodyProps = {
  caseId: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export type PaymentDetailsParams = {
  violationCaseId: string;
  finePaymentId: number;
  cost: string;
  isGGE: boolean;
  ri3: string;
  prettyRi3: string;
};

export type PreselectedFineParametrics = {
  companyEntityTypeId: number;
  companySizeTypeId: number;
  companyStatusId: number;
  legalTypeId: number;
  violationRepeatTypeId: number;
};

export type GetDashboardPathParams = {
  isSystemAdmin: boolean;
  isGGEAdmin: boolean;
  isChamber: boolean;
  isBusiness: boolean;
  isOrgAdmin: boolean;
  isSupervisorAssignee: boolean;
  mode: DashboardSectionEnum | undefined;
};

export enum ViolationTypeEnum {
  INCORPORATION_EYMS = 1,
  GEMI_NON_REGISTRATION = 2,
  FAILURE_TO_INCLUDE_REQUIRED_COMPANY_INFO = 3,
  LATE_SUBMISSION_GEMI_APPLICATION = 4,
  ONLINE_GEMI_SUBMISSION_VIOLATION = 5,
  MANDATORY_GEMI_DISCLOSURE_VIOLATION = 6,
  FAILURE_TO_SUBMIT_FINANCIAL_STATEMENTS = 7,
  CORPORATE_NAME_VIOLATION = 8,
}

export enum ViolationStatusEnum {
  DRAFT = 1,
  RECORDED_VIOLATION = 2,
  UNDER_REVIEW = 3,
  IN_THE_PROCESS_OF_HEARING = 4,
  DELETION = 5,
  FINE_TO_BE_FINALIZED = 6,
  FINALIZED_FINE = 7,
  COMPLETED = 8,
  TO_BE_CORRECTED = 9,
}

export enum EntityType {
  CHAMBER_UGEMI = 1,
  GGE_NON_LISTED = 2,
  GGE_LISTED = 3,
}

export enum ViolationType {
  E_YMS_ESTABLISHMENT = 1,
  GEMI_REGISTRATION_OMISSION = 2,
  DOCUMENT_INFO_OMISSION = 3,
  LATE_GEMI_SUBMISSION = 4,
  AUTOMATIC_ONLINE_REGISTRATION = 5,
  MANDATORY_GEMI_PUBLICATION = 6,
  FINANCIAL_STATEMENTS_OMISSION = 7,
  COMPANY_NAME_VIOLATION = 8,
}

export enum CorrectionPhaseEnum {
  CORRECTION = 1,
  CORRECTION_PAYMENT_FIRST_PHASE = 2,
  CORRECTION_PAYMENT_SECOND_PHASE = 3,
  CORRECTION_BY_CHAMBER = 4,
}
